import React, { FC, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import styled from "@emotion/styled";
import { Text } from "@radix-ui/themes";
import { api } from "@/modules/api";
import { atlasRoutes } from "@/app/router/routes";
import { WebClipsSearchForm } from "./web-clips-search-form";
import { WebClipsTable } from "./web-clips-table";
import { WebClipSummarySchema } from "@/domains/openapi/types";
import { Button } from "@/components/button";

export const WebClipsRootPage: FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const pageSize = 50;
  const initialQuery = searchParams.get("query") ?? "";
  const initialSpaceId = searchParams.get("space") ?? "";
  const [query, setQuery] = useState<string>(initialQuery);
  const [spaceId, setSpaceId] = useState<string>(initialSpaceId);

  useEffect(() => {
    setQuery(searchParams.get("query") ?? "");
    setSpaceId(searchParams.get("space") ?? "");
  }, [searchParams]);

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["web-clips-search", query, spaceId],
    queryFn: () =>
      api.post("/internal/v1/web-clips/search", {
        body: {
          query_string: query || undefined,
          filter_by_space_account_id: spaceId || undefined,
          limit: pageSize,
        },
      }),
    enabled: query.length > 0 || spaceId.length > 0,
    staleTime: 5 * 60 * 1000,
    retry: 1,
  });

  const handleView = (id: string) => {
    navigate(atlasRoutes.webClipsView({ params: { webClipId: id } }).path);
  };

  const handleSearch = (q: string, s: string) => {
    if (q) searchParams.set("query", q);
    else searchParams.delete("query");
    if (s) searchParams.set("space", s);
    else searchParams.delete("space");
    setSearchParams(searchParams);
  };

  const tableData = useMemo<WebClipSummarySchema[]>(() => data?.data?.results ?? [], [data]);

  return (
    <Container>
      <FlexHeader>
        <div>
          <h1>Web Clips</h1>
          <DescriptionText>Search clipped text or filter by space account.</DescriptionText>
        </div>
        <Button onClick={() => navigate(atlasRoutes.webClipsCreate({}).path)}>
          Create Web Clip
        </Button>
      </FlexHeader>
      <WebClipsSearchForm initialQuery={query} initialSpaceId={spaceId} onSearch={handleSearch} />
      {isLoading && <div>Loading...</div>}
      {isError && <div>Error fetching web clips: {error.message}</div>}
      {tableData.length > 0 ? (
        <WebClipsTable data={tableData} onViewWebClip={handleView} pageSize={pageSize} />
      ) : (
        !isLoading && (query || spaceId) && <div>No web clips found.</div>
      )}
    </Container>
  );
};

const Container = styled.div`
  width: 100%;
`;

const FlexHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
`;

const DescriptionText = styled(Text)`
  display: block;
  color: var(--gray-12);
`;
